import { Component, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { CONFIG_TOKEN } from 'core/constants';
import { IXMOptions } from 'core/interfaces';
import { AddonPassStatus, AddonStatus } from 'my-account/constants';
import { AddOnPassOption } from 'my-account/interface';
import { LineSummary } from 'store/account-summary/models';
import { CimaCore } from 'core/services';

@Component({
    selector: 'xm-add-remove-pass-cards',
    templateUrl: './add-remove-pass-cards.html',
    styleUrls: ['./add-remove-pass-cards.scss'],
    standalone: false
})
export class XmAddRemovePassCards implements OnChanges {
    @Input() public lines: LineSummary[];
    @Input() public addonLines: AddOnPassOption[];
    @Input() public pageData: CmsAddRemovePassData;
    @Input() public action: string; // 'add' or 'remove'
    @Output() public passSelectionChange: EventEmitter<string[]> = new EventEmitter<string[]>();

    public passForm: FormGroup;
    public checkboxes: FormArray;
    public passOptions: AddOnPassOption[] = [];
    public selectedLines: string[] = [];
    public AddonPassStatus: typeof AddonPassStatus = AddonPassStatus;
    public isResiInternationalEligibilityChanges: boolean = false;

    constructor(fb: FormBuilder, @Inject(CONFIG_TOKEN) config: IXMOptions, cimaCore: CimaCore) {
        this.isResiInternationalEligibilityChanges = config.ENABLE_INTERNATIONAL_ELIGIBILITY_CHANGES && !cimaCore.isBusinessUser;
        this.checkboxes = fb.array([]);
        this.passForm = fb.group({
            checkboxes: this.checkboxes
        });
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.addonLines?.currentValue.length > 0) {
            // Clear existing options and checkboxes
            this.passOptions = [];
            this.checkboxes.clear();

            // Reinitialize pass options based on the new addonLines input
            this.initializePassOptions();
        }
    }

    /**
     * Handles the selection change event for a pass option
     * 
     * @param option The pass option that changed
     * @param index The index of the checkbox in the form array
     */
    public onPassSelectionChange(option: AddOnPassOption, event: Event): void {

        if (option.isEditable) {
            const isChecked: boolean = (<HTMLInputElement>event.target).checked;
            const lineId: string = option.line_id;

            if (this.isPassSelectionValid(option, isChecked, this.selectedLines)) {
                if (!this.selectedLines.includes(lineId)) {
                    this.selectedLines.push(lineId);
                }
            } else {
                const idx: number = this.selectedLines.indexOf(lineId);
                if (idx !== -1) {
                    this.selectedLines.splice(idx, 1);
                }
            }

            this.passSelectionChange.emit(this.selectedLines);
        }
    }

    /**
     * Determines if a pass option selection is valid based on the given conditions.
     * @param option The pass option.
     * @param isChecked Whether the checkbox is checked.
     * @param selectedLines The currently selected line IDs.
     * @returns Boolean indicating if the selection is valid.
     */
    public isPassSelectionValid(option: AddOnPassOption, isChecked: boolean, selectedLines: string[]): boolean {
        const lineId: string = option.line_id;
        const isSelected: boolean = selectedLines.includes(lineId);

        if (
            option.status === AddonStatus.ACTIVE &&
            this.action === 'remove'
        ) {
            if (!isChecked && !isSelected) {
                return true;
            }
            if (isChecked && isSelected) {
                return false;
            }
        }

        if (
            option.status === AddonStatus.INACTIVE &&
            this.action === 'add'
        ) {
            if (isChecked && !isSelected) {
                return true;
            }
            if (!isChecked && isSelected) {
                return false;
            }
        }

        return false;
    }


    /**
     * Initializes the pass options for each line
     */
    private initializePassOptions(): void {
        if (this.addonLines.length > 0 && this.lines.length > 0) {
            this.addonLines.forEach((line: AddOnPassOption) => {
                const matchedLine: LineSummary = this.lines.find((l: LineSummary) => l.id.key === line.line_id);
                if (!matchedLine) {
                    return; // Skip if no matching line found
                }
                
                const passOption: AddOnPassOption = {
                    line_id: line.line_id,
                    status: line.status,
                    displayName: line.displayName,
                    lineNumber: line.lineNumber,
                    isSelected: (line.status === AddonStatus.ACTIVE || line.status === AddonStatus.INCLUDED),
                    isEditable: this.isPassEditable(line), 
                    color: matchedLine.color,
                    iconLetter: line.displayName[0]
                };

                this.passOptions.push(passOption);

                // Add a checkbox control for this option
                const control: FormControl = new FormControl(passOption.isSelected);
                this.checkboxes.push(control);
            });
        }
    }

    private isPassEditable(option: AddOnPassOption): boolean {
        if (option.status === AddonStatus.ACTIVE && this.action === 'remove') {
            return true;
        } else if (option.status === AddonStatus.INACTIVE && this.action === 'add') {
            return true;
        } else {
            return false;
        }
    }
}
